import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Newspaper, Plus, User, X, Trash2 } from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

function News() {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news');
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Error al cargar noticias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/news', formData);
      toast.success('Noticia publicada');
      setDialogOpen(false);
      setFormData({
        title: '',
        content: '',
        image_url: ''
      });
      fetchNews();
    } catch (error) {
      console.error('Error creating news:', error);
      toast.error('Error al publicar noticia');
    }
  };

  const handleDelete = async (newsId, e) => {
    e.stopPropagation();
    if (!confirm('¿Eliminar esta noticia?')) return;
    
    setDeleting(newsId);
    try {
      await api.delete(`/news/${newsId}`);
      toast.success('Noticia eliminada');
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error('Error al eliminar noticia');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="news-page">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Noticias</h1>
            <p className="text-muted-foreground">Mantente informado de las últimas novedades</p>
          </div>
          
          {user?.role === 'agent' && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-news-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Noticia
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Publicar Noticia</DialogTitle>
                  <DialogDescription>Comparte información importante con todos</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      data-testid="news-title-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="image_url">URL de Imagen</Label>
                    <Input
                      id="image_url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://ejemplo.com/imagen.jpg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="content">Contenido *</Label>
                    <Textarea
                      id="content"
                      data-testid="news-content-textarea"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={8}
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="submit-news-button">Publicar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {news.length === 0 ? (
          <Card className="bento-card">
            <CardContent className="p-12 text-center">
              <Newspaper className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No hay noticias publicadas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item) => (
              <Card 
                key={item.news_id} 
                className="bento-card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group relative"
                onClick={() => setSelectedNews(item)}
                data-testid={`news-card-${item.news_id}`}
              >
                {/* Delete button for agents */}
                {user?.role === 'agent' && (
                  <button
                    onClick={(e) => handleDelete(item.news_id, e)}
                    disabled={deleting === item.news_id}
                    className="absolute top-2 right-2 z-10 p-2 bg-white/90 hover:bg-red-50 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-news-${item.news_id}`}
                  >
                    <Trash2 className={`h-4 w-4 ${deleting === item.news_id ? 'text-gray-400 animate-spin' : 'text-red-500'}`} />
                  </button>
                )}
                {item.image_url && (
                  <div className="aspect-video w-full overflow-hidden bg-secondary">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <CardContent className="p-5">
                  <h3 className="text-xl font-bold mb-2 line-clamp-2 tracking-tight">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {item.content}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      <span>{item.author_name}</span>
                    </div>
                    <span>
                      {new Date(item.published_at).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal para ver noticia completa */}
        {selectedNews && (
          <div 
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedNews(null)}
          >
            <div 
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#4F4F4F] pr-8">{selectedNews.title}</h2>
                <button 
                  onClick={() => setSelectedNews(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="close-news-modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {selectedNews.image_url && (
                <div className="aspect-video w-full overflow-hidden">
                  <img
                    src={selectedNews.image_url}
                    alt={selectedNews.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6 pb-4 border-b">
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    <span>{selectedNews.author_name}</span>
                  </div>
                  <span>•</span>
                  <span>
                    {new Date(selectedNews.published_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                
                <div className="prose prose-sm max-w-none">
                  {selectedNews.content.split('\n').map((paragraph, idx) => (
                    <p key={idx} className="mb-4 text-[#4F4F4F] leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default News;